import {
    addDaysToKey,
    dayKey,
    detectTimeZone,
    isValidZone,
    offsetLabel,
    timeLabel,
    zonedParts,
    zonedToUtc
} from '../../../utils/calendarTime';

export const EVENTS_TINT = '#E8871E';

export const ZONE_KEY = 'franklyStaffEventsZone';

export const EVENT_TYPES = [
    { name: 'Event Creator', color: '#d37e00' },
    { name: 'Frankly', color: '#88271c' },
    { name: 'Community', color: '#b9a134' }
];

export const TYPE_COLORS = EVENT_TYPES.reduce((acc, type) => {
    acc[type.name] = type.color;
    return acc;
}, {});

export const typeColor = (name) => TYPE_COLORS[name] || '#4a4a4a';

export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const WEEKDAY_INITIAL = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export const readStoredZone = () => {
    try {
        const stored = window.localStorage.getItem(ZONE_KEY);
        if (stored && isValidZone(stored)) return stored;
    } catch {}

    return detectTimeZone();
};

export const writeStoredZone = (zone) => {
    try {
        window.localStorage.setItem(ZONE_KEY, zone);
    } catch {}
};

export const pad = (value) => String(value).padStart(2, '0');

export const weekStartKey = (key) => {
    const [year, month, day] = key.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    const shift = (date.getUTCDay() + 6) % 7;

    return addDaysToKey(key, -shift);
};

export const weekKeys = (startKey) =>
    Array.from({ length: 7 }, (_, index) => addDaysToKey(startKey, index));

export const shiftMonthKey = (key, delta) => {
    const [year, month] = key.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1 + delta, 1));

    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-01`;
};

export const rangeForView = (mode, anchorKey) => {
    if (mode === 'day') return { fromKey: anchorKey, toKey: addDaysToKey(anchorKey, 1) };

    if (mode === 'week') {
        const start = weekStartKey(anchorKey);
        return { fromKey: start, toKey: addDaysToKey(start, 7) };
    }

    const [year, month] = anchorKey.split('-').map(Number);
    const firstKey = `${year}-${pad(month)}-01`;
    const gridStart = weekStartKey(firstKey);

    return { fromKey: gridStart, toKey: addDaysToKey(gridStart, 42) };
};

export const localDay = (event, zone) => dayKey(event.startTime, zone);

export const localMinutes = (value, zone) => {
    const parts = zonedParts(value, zone);
    return (parts.hour * 60) + parts.minute;
};

export const eventTimeLabel = (event, zone) =>
    `${timeLabel(event.startTime, zone)} - ${timeLabel(event.endTime, zone)}`;

export const durationLabel = (minutes) => {
    const safe = Math.max(0, Math.round(minutes || 0));
    const hours = Math.floor(safe / 60);
    const mins = safe % 60;

    if (hours && mins) return `${hours}h ${mins}m`;
    if (hours) return `${hours}h`;

    return `${mins}m`;
};

export const readableRange = (fromKey, toKey) => {
    const format = (key) => {
        const [year, month, day] = key.split('-').map(Number);
        return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC'
        });
    };

    return `${format(fromKey)} to ${format(addDaysToKey(toKey, -1))}`;
};

export const readableMonth = (key) => {
    const [year, month] = key.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC'
    });
};

export const eventsByDay = (events, zone) => {
    const map = new Map();

    for (const event of events) {
        const key = localDay(event, zone);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(event);
    }

    for (const list of map.values()) {
        list.sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));
    }

    return map;
};

export const canManageEvent = (event, userData) => {
    if (!event || !userData) return false;
    if (userData.hasAdminRole) return true;
    if (event.type === 'Frankly') return event.addedBy === userData.userId;

    return true;
};

export const utcNote = (event) =>
    `${timeLabel(event.startTime, 'UTC')} - ${timeLabel(event.endTime, 'UTC')} UTC`;

export {
    addDaysToKey,
    dayKey,
    detectTimeZone,
    isValidZone,
    offsetLabel,
    timeLabel,
    zonedParts,
    zonedToUtc
};
